import "./Modals.scss";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Modal } from "antd";
import { CheckOutlined, CloseOutlined, StopOutlined } from "@ant-design/icons";
import {
  LeaveRequest,
  LeaveRequestPending,
  ModalProps,
  User,
  UserSignature,
} from "../../utils/types";
import {
  countWorkdays,
  fetchData,
  getErrorMessage,
  Notify,
  postData,
  RequestStatus,
} from "../../utils/constants";
import { apiClient } from "../../utils/httpClient";
import { DownloadPDFButton, leavePDF } from "../Buttons/DownloadPDFBtn";
import { ViewDocumentsBtn } from "../Buttons/ViewDocumentsBtn";
import { useUser } from "../../context/UserContext";
import { resolvePdfDaysSplit } from "../../utils/pdfDaysSplit";

const statusLabels: Record<RequestStatus, string> = {
  APPROVE: "Approve request",
  REJECT: "Reject request",
  CANCEL: "Send Cancel request",
  AWAITING_MANAGER: "Pending request",
};

const statusIcons: Record<RequestStatus, React.ReactNode> = {
  APPROVE: <CheckOutlined />,
  REJECT: <CloseOutlined />,
  CANCEL: <StopOutlined />,
  AWAITING_MANAGER: <CheckOutlined />,
};

type RequestStatusModalProps = ModalProps & {
  request: LeaveRequest | LeaveRequestPending | null;
  status: RequestStatus;
  source?: "self" | "default";
  onCancelConfirm?: () => void;
};

type FormData = {
  requestId: number;
  approved: boolean;
  managerComment: string;
  leavePDF?: string;
};

const isPendingRequest = (
  req: LeaveRequest | LeaveRequestPending | null,
): req is LeaveRequestPending => {
  return req !== null && "originalRequestId" in req;
};

const isLeaveRequest = (
  req: LeaveRequest | LeaveRequestPending | null,
): req is LeaveRequest => req !== null && "leavePDF" in req;

const RequestStatusModal = ({
  isOpen,
  setIsOpen,
  request,
  status,
  source,
  onCancelConfirm,
}: RequestStatusModalProps) => {
  const { authUser } = useUser();
  const [comment, setComment] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [employeeUser, setEmployeeUser] = useState<User | null>(null);
  const [substituteUser, setSubstituteUser] = useState<User | null>(null);
  const [userSign, setUserSign] = useState<string>("");
  const [pdf, setPDF] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [mySignature, setMySignature] = useState<UserSignature | null>(null);
  const isAnnualLeave =
    !isPendingRequest(request) && request?.requestType === "ANNUAL_LEAVE";
  const isAdministrativStep =
    !isPendingRequest(request) && request?.requestStatus === "AWAITING_ADMINISTRATIV";
  // A Manager signature (and the fully-rendered, two-signature PDF) is
  // captured whenever a Manager approves - any request type, whichever
  // position the Manager step occupies (first, for Marriage requested before
  // the wedding; final, for everything else). Administrativ never signs.
  const showSign =
    status === RequestStatus.APPROVE &&
    !isPendingRequest(request) &&
    request?.requestStatus === "AWAITING_MANAGER";
  const pdfDaysSplit =
    isLeaveRequest(request) && request
      ? resolvePdfDaysSplit(request as unknown as Record<string, unknown>)
      : {
          previousYearDaysUsedForRequest: 0,
          currentYearDaysUsedForRequest: 0,
        };

  const getUsers = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/getAllUsers`,
        setUsers,
      );
    } catch (error) {
      console.error("Error while fetching data:", error);
      Notify("error", "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRequestStatus = useCallback(async () => {
    if (!request) {
      Notify("error", "No request selected.");
      return;
    }

    setLoading(true);
    try {
      if (status === RequestStatus.CANCEL && source === "self") {
        // "Still pending" covers every not-yet-decided status across both
        // Annual Leave (AWAITING_MANAGER) and the 5 new types
        // (AWAITING_ADMINISTRATIV, AWAITING_MANAGER, RETURNED_FOR_CORRECTION) -
        // those can be withdrawn directly, no review needed.
        const isPending = [
          "AWAITING_MANAGER",
          "AWAITING_ADMINISTRATIV",
          "RETURNED_FOR_CORRECTION",
        ].includes(request.requestStatus);
        let cancelApi: string;
        if (isAnnualLeave) {
          cancelApi = isPending
            ? `${process.env.REACT_APP_API_URL}/cancel-pending-request`
            : `${process.env.REACT_APP_API_URL}/cancel-leave-request`;
        } else {
          cancelApi = isPending
            ? `${process.env.REACT_APP_API_URL}/cancel-pending-request-typed`
            : `${process.env.REACT_APP_API_URL}/cancel-leave-request-typed`;
        }
        const payload = {
          requestId: request.id,
          comment,
        };
        await postData(cancelApi, payload);
        Notify(
          "success",
          isPending
            ? "Pending request cancelled successfully!"
            : "Cancellation request sent successfully!",
        );
      } else {
        const formData: FormData = {
          requestId: isPendingRequest(request)
            ? request.originalRequestId
            : request?.id,
          approved: status === RequestStatus.APPROVE ? true : false,
          managerComment: comment,
        };

        if (showSign) {
          formData.leavePDF = pdf;
        }

        let api: string;
        if (isPendingRequest(request)) {
          const isNewTypeCancellation = request.requestType !== "ANNUAL_LEAVE";
          if (!isNewTypeCancellation) {
            api = `${process.env.REACT_APP_API_URL}/review-cancellation/${request?.id}`;
          } else if (request.requestStatus === "AWAITING_ADMINISTRATIV_CANCELLATION") {
            api = `${process.env.REACT_APP_API_URL}/review-cancellation-administrativ/${request?.id}`;
          } else {
            api = `${process.env.REACT_APP_API_URL}/review-cancellation-new-type/${request?.id}`;
          }
        } else if (isAnnualLeave) {
          api = `${process.env.REACT_APP_API_URL}/review`;
        } else if (isAdministrativStep) {
          api = `${process.env.REACT_APP_API_URL}/review-administrativ`;
        } else {
          api = `${process.env.REACT_APP_API_URL}/review-new-type`;
        }

        await postData(api, formData);
        Notify("success", `Request ${status}ED successfully!`);
      }

      if (onCancelConfirm) {
        onCancelConfirm();
      }
      setIsOpen(false);
    } catch (err) {
      console.error(`Request ${status} error:`, err);
      Notify(
        "error",
        getErrorMessage(
          err,
          `Oops! Something went wrong while ${status} request.`,
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [
    comment,
    isAdministrativStep,
    isAnnualLeave,
    onCancelConfirm,
    pdf,
    request,
    setIsOpen,
    showSign,
    source,
    status,
  ]);

  useEffect(() => {
    if (isOpen) {
      setComment("");
      setPDF("");
      setEmployeeUser(null);
      setSubstituteUser(null);
      setUsers([]);
      setUserSign("");
      setMySignature(null);

      getUsers();
    }
  }, [getUsers, isOpen]);

  useEffect(() => {
    if (!isOpen || !showSign) return;

    apiClient
      .get<UserSignature>(`${process.env.REACT_APP_API_URL}/users/me/signature`)
      .then((response) => setMySignature(response.data))
      .catch((error) => {
        console.error("Failed to load saved signature:", error);
      });
  }, [isOpen, showSign]);

  useEffect(() => {
    if (isOpen && isLeaveRequest(request)) {
      if (request?.leavePDF) setUserSign(request?.leavePDF);
      leavePDF.subscribe({
        next: (val) => {
          setPDF(val);
        },
      });
    }
  }, [isOpen, request]);

  useEffect(() => {
    if (showSign && users.length && !substituteUser && request) {
      setLoading(true);

      if (request.substituteName) {
        const normalizedSubstituteName = request.substituteName
          .replace(/\s+/g, "")
          .toLowerCase();

        const foundSubstitute = users.find((user) => {
          const name1 = (user.firstName + user.lastName)
            .replace(/\s+/g, "")
            .toLowerCase();
          const name2 = (user.lastName + user.firstName)
            .replace(/\s+/g, "")
            .toLowerCase();
          return (
            name1 === normalizedSubstituteName ||
            name2 === normalizedSubstituteName
          );
        });

        if (foundSubstitute) {
          setSubstituteUser(foundSubstitute);
        }
      }

      const normalizedEmployeeName = request.employeeName
        .replace(/\s+/g, "")
        .toLowerCase();

      const foundEmployee = users.find((user) => {
        const name1 = (user.firstName + user.lastName)
          .replace(/\s+/g, "")
          .toLowerCase();
        const name2 = (user.lastName + user.firstName)
          .replace(/\s+/g, "")
          .toLowerCase();

        return (
          name1 === normalizedEmployeeName || name2 === normalizedEmployeeName
        );
      });

      if (foundEmployee) {
        setEmployeeUser(foundEmployee);
      }

      setLoading(false);
    }
  }, [request, users, substituteUser, authUser, showSign]);

  return (
    <Modal
      className="md"
      title={`${showSign ? "Sign and " : ""}Leave a comment`}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type={
            status === RequestStatus.APPROVE ||
            status === RequestStatus.AWAITING_MANAGER
              ? "primary"
              : "default"
          }
          danger={status === RequestStatus.REJECT}
          icon={statusIcons[status]}
          onClick={handleRequestStatus}
          loading={loading}
          disabled={loading || (showSign && !pdf)}
        >
          {statusLabels[status]}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        {!isPendingRequest(request) && !!request?.documents?.length && (
          <Form.Item label="Uploaded documents">
            <ViewDocumentsBtn documents={request.documents} />
          </Form.Item>
        )}
        <Form.Item label="Comment">
          <Input value={comment} onChange={(e) => setComment(e.target.value)} />
        </Form.Item>
        {showSign && (
          <Form.Item label="Request signature">
            <DownloadPDFButton
              user={employeeUser}
              workDays={
                request && isLeaveRequest(request)
                  ? request.requestedDays ||
                    countWorkdays(
                      request.startDate
                        ? new Date(request.startDate)
                        : new Date(),
                      request.endDate ? new Date(request.endDate) : new Date(),
                    )
                  : 0
              }
              startDate={
                request?.startDate ? new Date(request.startDate) : new Date()
              }
              endDate={
                request?.endDate ? new Date(request?.endDate) : new Date()
              }
              substitute={substituteUser}
              userSignature={userSign}
              autoSignature={
                mySignature?.useSignatureAuto
                  ? (mySignature.signatureImage ?? undefined)
                  : undefined
              }
              open={isOpen}
              previousYearDaysUsedForRequest={
                pdfDaysSplit.previousYearDaysUsedForRequest
              }
              currentYearDaysUsedForRequest={
                pdfDaysSplit.currentYearDaysUsedForRequest
              }
              requestType={
                !isPendingRequest(request) ? request?.requestType : undefined
              }
              unpaidLeaveReason={
                !isPendingRequest(request)
                  ? request?.unpaidLeaveReason
                  : undefined
              }
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default RequestStatusModal;
